import React from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Add, ErrorOutline, InfoOutlined, PlayArrow, WarningAmber } from '@mui/icons-material';
import type { ManagerResponse } from '@/api/generated/model';

const CREATE_LINKS: Array<{ key: keyof ManagerResponse['create']; label: string; href: string }> = [
  { key: 'tournament', label: 'Турнир', href: '/tournaments/new' },
  { key: 'team', label: 'Команда', href: '/teams/new' },
  { key: 'player', label: 'Игрок', href: '/players/new' },
  { key: 'venue', label: 'Площадка', href: '/venues/new' },
  { key: 'official', label: 'Судья', href: '/officials/new' },
  { key: 'organization', label: 'Организация', href: '/organizations/new' },
];

const severityIcon = (severity: string) =>
  severity === 'error' ? (
    <ErrorOutline color="error" />
  ) : severity === 'warning' ? (
    <WarningAmber color="warning" />
  ) : (
    <InfoOutlined color="info" />
  );

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h5" sx={{ mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/** Главная в режиме «Админ»: что требует внимания, матчи сегодня, мои ресурсы, создание. */
export const ManagerPanel: React.FC<{ manager: ManagerResponse }> = ({ manager }) => {
  const navigate = useNavigate();
  const tournaments = manager.resources.tournaments ?? [];
  const createLinks = CREATE_LINKS.filter((l) => manager.create[l.key]);

  return (
    <Paper sx={{ p: 2, mb: 3, borderTop: 3, borderColor: 'manage.main' }}>
      <Typography variant="h3" color="manage.main" sx={{ mb: 2 }}>
        Управление
      </Typography>

      {manager.attention.length > 0 && (
        <Section title="Требует внимания">
          <List disablePadding>
            {manager.attention.map((item) => (
              <ListItemButton key={item.id} onClick={() => navigate(item.href)} sx={{ minHeight: 56, borderRadius: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>{severityIcon(item.severity)}</ListItemIcon>
                <ListItemText primary={item.title} secondary={item.subtitle} />
              </ListItemButton>
            ))}
          </List>
        </Section>
      )}

      {manager.today.length > 0 && (
        <Section title="Сегодня">
          <List disablePadding>
            {manager.today.map((item) => (
              <ListItemButton key={item.matchId} onClick={() => navigate(item.href)} sx={{ minHeight: 56, borderRadius: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <PlayArrow sx={{ color: 'manage.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  secondary={[
                    item.scheduledAt ? new Date(item.scheduledAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : null,
                    item.subtitle,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
                <Chip size="small" color="manage" label={item.action === 'live' ? 'Вести' : 'Внести счёт'} />
              </ListItemButton>
            ))}
          </List>
        </Section>
      )}

      {tournaments.length > 0 && (
        <Section title="Мои турниры">
          <List disablePadding>
            {tournaments.slice(0, 6).map((t) => (
              <ListItemButton key={t.id} onClick={() => navigate(`/tournaments/${t.id}?tab=workflow`)} sx={{ minHeight: 56, borderRadius: 1 }}>
                <ListItemText primary={t.name} secondary={t.nextStep ? `Дальше: ${t.nextStep}` : undefined} />
              </ListItemButton>
            ))}
          </List>
          {tournaments.length > 6 && (
            <Button onClick={() => navigate('/tournaments?mine=1')} sx={{ minHeight: 40 }}>
              Все мои турниры
            </Button>
          )}
        </Section>
      )}

      {createLinks.length > 0 && (
        <Section title="Создать">
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {createLinks.map((l) => (
              <Button key={l.key} variant="outlined" color="manage" startIcon={<Add />} onClick={() => navigate(l.href)} sx={{ minHeight: 44 }}>
                {l.label}
              </Button>
            ))}
          </Stack>
        </Section>
      )}

      {manager.attention.length === 0 && manager.today.length === 0 && tournaments.length === 0 && (
        <Typography color="text.secondary">Задач нет. Создайте турнир, чтобы начать.</Typography>
      )}
    </Paper>
  );
};
